import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import api from '../../services/api.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';
import {
  FRAME_OPTIONS, FRAME_LABELS, AUDIENCE_OPTIONS, AUDIENCE_LABELS, SOURCE_LABELS, ORDER_STATUS,
  emptyCampaignForm, campaignToForm, campaignStateLabel, formatDay, downloadBlob,
} from './gemaraOrdersShared';

/** הזמנות גמרא — העיצוב הקודם. אותם endpoints כמו GemaraOrdersNext. */
export default function GemaraOrders() {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [orders, setOrders] = useState([]);
  const [byCity, setByCity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/gemara-orders/campaigns');
      const list = data.data || [];
      setCampaigns(list);
      setSelectedId((cur) => (cur && list.some((c) => c._id === cur) ? cur : list[0]?._id || ''));
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה בטעינת הכרכים');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const loadOrders = useCallback(async () => {
    if (!selectedId) { setOrders([]); setByCity([]); return; }
    try {
      const [o, c] = await Promise.all([
        api.get('/gemara-orders', { params: { campaignId: selectedId } }),
        api.get('/gemara-orders/by-city', { params: { campaignId: selectedId } }),
      ]);
      setOrders(o.data.data || []);
      setByCity(c.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה בטעינת ההזמנות');
    }
  }, [selectedId]);
  useEffect(() => { loadOrders(); }, [loadOrders]);

  const selected = campaigns.find((c) => c._id === selectedId) || null;

  const run = async (fn) => {
    setError('');
    try {
      await fn();
      loadCampaigns();
      loadOrders();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה');
    }
  };

  const saveCampaign = async () => {
    setSaving(true);
    await run(async () => {
      if (dialog.id) await api.put(`/gemara-orders/campaigns/${dialog.id}`, dialog.form);
      else {
        const { data } = await api.post('/gemara-orders/campaigns', dialog.form);
        setSelectedId(data._id);
      }
      setDialog(null);
    });
    setSaving(false);
  };

  const exportExcel = () => run(async () => {
    const res = await api.get('/gemara-orders/export-excel', { params: { campaignId: selectedId }, responseType: 'blob' });
    downloadBlob(res.data, `הזמנות_גמרא_${selected?.title || ''}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  });

  const setForm = (patch) => setDialog((d) => ({ ...d, form: { ...d.form, ...patch } }));
  const state = selected ? campaignStateLabel(selected) : null;

  return (
    <Box>
      <AdminPageHeader
        title="הזמנות גמרא"
        sub="כרך פתוח מוצע לנבחנים בתחילת המבחן ובסיומו ובאתר; כרך פתוח לכולם גם בשלוחה 1/4 · 2/4. כל חבר מזמין פעם אחת לכל כרך."
        actions={<Button variant="contained" onClick={() => setDialog({ id: null, form: emptyCampaignForm() })}>כרך חדש</Button>}
      />
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {loading ? <CircularProgress /> : !campaigns.length ? (
        <Typography color="text.secondary">אין כרכים להזמנה. צרו כרך ופתחו אותו להזמנה.</Typography>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Select size="small" value={selectedId} onChange={(e) => setSelectedId(e.target.value)} sx={{ minWidth: 240 }}>
              {campaigns.map((c) => <MenuItem key={c._id} value={c._id}>{`${c.title} (${c.orderCount})`}</MenuItem>)}
            </Select>
            {selected ? (
              <>
                <Chip label={state.label} color={selected.isOpenNow ? 'success' : 'default'} />
                <Typography variant="body2" color="text.secondary">
                  {[
                    (selected.frames || []).map((f) => FRAME_LABELS[f]).join(' · '),
                    AUDIENCE_LABELS[selected.audience || 'examinees'],
                    selected.opensAt ? `נפתח ${formatDay(selected.opensAt)}` : '',
                    selected.closesAt ? `אחרון להזמנה ${formatDay(selected.closesAt)}` : '',
                  ].filter(Boolean).join(' · ')}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {selected.status === 'open'
                  ? <Button onClick={() => run(() => api.put(`/gemara-orders/campaigns/${selected._id}`, { status: 'closed' }))}>סגירת ההזמנה</Button>
                  : <Button variant="contained" onClick={() => run(() => api.put(`/gemara-orders/campaigns/${selected._id}`, { status: 'open' }))}>פתיחה להזמנה</Button>}
                <Button onClick={() => setDialog({ id: selected._id, form: campaignToForm(selected) })}>עריכה</Button>
                <Button
                  color="error"
                  disabled={!!selected.orderCount}
                  onClick={() => window.confirm(`למחוק את הכרך «${selected.title}»?`) && run(() => api.delete(`/gemara-orders/campaigns/${selected._id}`))}
                >
                  מחיקה
                </Button>
                <Button variant="outlined" disabled={!byCity.length} onClick={exportExcel}>ייצוא Excel לשליחים</Button>
              </>
            ) : null}
          </Paper>

          {byCity.length ? (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography fontWeight={700} sx={{ mb: 1 }}>לפי עיר</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {byCity.map((r) => <Chip key={r.city} label={`${r.city}: ${r.total} (נמסרו ${r.delivered})`} />)}
              </Box>
            </Paper>
          ) : null}

          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>שם</TableCell>
                  <TableCell>עיר</TableCell>
                  <TableCell>טלפון</TableCell>
                  <TableCell>הוזמן דרך</TableCell>
                  <TableCell>תאריך</TableCell>
                  <TableCell>סטטוס</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o._id}>
                    <TableCell>{o.memberName || '—'}</TableCell>
                    <TableCell>{o.city || 'ללא עיר'}</TableCell>
                    <TableCell dir="ltr">{o.phone || '—'}</TableCell>
                    <TableCell>{SOURCE_LABELS[o.source] || o.source}{o.frame ? ` · ${FRAME_LABELS[o.frame] || ''}` : ''}</TableCell>
                    <TableCell>{formatHebrewDateTime(o.createdAt)}</TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={o.status}
                        onChange={(e) => run(() => api.patch(`/gemara-orders/${o._id}`, { status: e.target.value }))}
                      >
                        {Object.entries(ORDER_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
                {!orders.length ? (
                  <TableRow><TableCell colSpan={6}><Typography color="text.secondary">אין עדיין הזמנות לכרך הזה</Typography></TableCell></TableRow>
                ) : null}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <Dialog open={!!dialog} onClose={() => !saving && setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>{dialog?.id ? 'עריכת כרך' : 'כרך חדש להזמנה'}</DialogTitle>
        {dialog ? (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
            <TextField label="שם הכרך" value={dialog.form.title} onChange={(e) => setForm({ title: e.target.value })} placeholder="מסכת חולין כרך ב" autoFocus />
            <Box>
              <Typography variant="body2" color="text.secondary">מוצע לנבחני</Typography>
              {FRAME_OPTIONS.map((f) => (
                <FormControlLabel
                  key={f.value}
                  label={f.label}
                  control={(
                    <Checkbox
                      checked={dialog.form.frames.includes(f.value)}
                      onChange={(e) => setForm({
                        frames: e.target.checked
                          ? [...new Set([...dialog.form.frames, f.value])]
                          : dialog.form.frames.filter((x) => x !== f.value),
                      })}
                    />
                  )}
                />
              ))}
            </Box>
            <TextField
              select
              label="מי יכול להזמין"
              value={dialog.form.audience}
              onChange={(e) => setForm({ audience: e.target.value })}
              helperText={AUDIENCE_OPTIONS.find((a) => a.value === dialog.form.audience)?.hint}
            >
              {AUDIENCE_OPTIONS.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
            </TextField>
            <TextField type="date" label="פתיחה (רשות)" InputLabelProps={{ shrink: true }} value={dialog.form.opensAt} onChange={(e) => setForm({ opensAt: e.target.value })} />
            <TextField type="date" label="יום אחרון להזמנה (רשות)" InputLabelProps={{ shrink: true }} value={dialog.form.closesAt} onChange={(e) => setForm({ closesAt: e.target.value })} helperText={'ביום שלפניו מושמעת בקו ההודעה "ההזמנה נסגרת מחר"'} />
            <TextField label="הערות" multiline minRows={2} value={dialog.form.notes} onChange={(e) => setForm({ notes: e.target.value })} />
          </DialogContent>
        ) : null}
        <DialogActions>
          <Button disabled={saving} onClick={() => setDialog(null)}>ביטול</Button>
          <Button variant="contained" disabled={saving || !dialog?.form.title.trim() || !dialog?.form.frames?.length} onClick={saveCampaign}>
            {dialog?.id ? 'שמירה' : 'יצירה'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
